import React from 'react'
import { Button, Layout, Progress, Typography, Input, Spin, Alert, Drawer } from 'antd'
import OtpAuthFlow from '../components/OtpAuthFlow.jsx'
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { CloseOutlined, MenuOutlined } from '@ant-design/icons';
import { useCartStore } from '../state/cartStore'
import { formatAmount } from '../utils/amount'
import { apiUrl } from '../config/api'

const { Header, Content, Footer } = Layout

export default function MainLayout() {
    const navigate = useNavigate()
    const location = useLocation()
    const containerStyle = { margin: '0 auto', paddingInline: 'clamp(12px, 4vw, 24px)' }
    const bodyStyle = { maxWidth: 1280, margin: '0 auto' };

    // General configs loading
    const [cfgLoading, setCfgLoading] = React.useState(true)
    const [cfgError, setCfgError] = React.useState('')
    const [configs, setConfigs] = React.useState(null)

    React.useEffect(() => {
        let alive = true
        async function load() {
            setCfgLoading(true)
            setCfgError('')
            try {
                const res = await fetch(apiUrl('/api/general-configs'), { headers: { 'Accept': 'application/json' } })
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                const data = await res.json()
                if (alive) setConfigs(data || {})
            } catch {
                if (alive) setCfgError('failed')
            } finally {
                if (alive) setCfgLoading(false)
            }
        }
        load()
    }, [])

    // Help format 24h time HH:mm to Arabic 12h format
    const formatTimeArabic = (hhmm) => {
        if (!hhmm) return '';
        try {
            const [h, m] = hhmm.split(':').map(Number);
            const period = h < 12 ? 'ص' : 'م';
            let hour = h % 12;
            if (hour === 0) hour = 12;
            return `${hour}:${String(m).padStart(2, '0')} ${period}`;
        } catch { return hhmm; }
    };

    // Maintenance/coming soon gate flags
    const underMaintenanceStr = String((configs || {}).underMaintenance || '').toLowerCase();
    const comingSoonStr = String((configs || {}).comingSoon || '').toLowerCase();
    const outSideWorkingHoursStr = String((configs || {}).outSideWorkingHours || '').toLowerCase();

    const underMaintenance = underMaintenanceStr === 'true' || underMaintenanceStr === '1'
    const comingSoon = (comingSoonStr === 'true' || comingSoonStr === '1') && location.pathname === '/'
    const outSideWorkingHours = outSideWorkingHoursStr === 'true'
    const showGate = cfgLoading || !!cfgError || underMaintenance || comingSoon

    const shift1Start = (configs || {}).shift1Start;
    const shift1End = (configs || {}).shift1End;
    const shift2Start = (configs || {}).shift2Start;
    const shift2End = (configs || {}).shift2End;

    const gateView = (
        <Layout style={{ minHeight: '100vh' }}>
            <Content style={{ display: 'grid', placeItems: 'center', padding: 16 }}>
                {cfgLoading && (
                    <div style={{ textAlign: 'center' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12 }}>جارٍ التحميل...</div>
                    </div>
                )}
                {!cfgLoading && cfgError && (
                    <div style={{ maxWidth: 480, width: '100%' }}>
                        <Alert type="error" message="فشل تحميل الإعدادات العامة" description="الرجاء المحاولة لاحقاً" showIcon />
                        <div style={{ textAlign: 'center', marginTop: 16 }}>
                            <Button type="primary" onClick={() => window.location.reload()}>إعادة المحاولة</Button>
                        </div>
                    </div>
                )}
                {!cfgLoading && !cfgError && comingSoon && (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                        <div style={{ display: 'grid', placeItems: 'center', gap: 16 }}>
                            <img
                                src="/logo.png"
                                alt="Logo"
                                style={{ width: 'min(60vw, 160px)', height: 'auto', objectFit: 'contain' }}
                                onError={(e) => { e.currentTarget.style.display = 'none' }}
                            />
                            <div>
                                <div style={{ fontSize: 'clamp(28px, 10vw, 56px)', lineHeight: 1.2, fontWeight: 800 }}>قريبا...</div>
                                <div style={{ marginTop: 8, fontSize: 'clamp(16px, 5vw, 22px)', color: '#555', fontWeight: 600 }}>
                                    كلشي بدك اياه .. بتكّة بيكون عندك
                                </div>
                                <div style={{ marginTop: 8, fontSize: 'clamp(16px, 5vw, 16px)', color: '#555', fontWeight: 600 }}>
                                    مطاعم، كوكتيل، حلويات، خضار وفواكه، مخبوزات، حلويات، مشاوي، شحن كهرباء، غاز، طلب تاكسي، هدايا وعطور، مكتبة وقرطاسية … وغيرها الكثير
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {!cfgLoading && !cfgError && underMaintenance && (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                        <Typography.Title level={3}>الموقع تحت الصيانة</Typography.Title>
                        <Typography.Paragraph>نحن نعمل على تحسين التجربة حالياً. الرجاء العودة لاحقاً.</Typography.Paragraph>
                    </div>
                )}
            </Content>
        </Layout>
    )

    // Cart summary

    // Cart summary
    const items = useCartStore(s => s.items)
    const clear = useCartStore(s => s.clear)
    const { totalCount, totalAfter } = React.useMemo(() => {
        const list = Object.values(items || {})
        const totalCount = list.reduce((s, it) => s + (it.quantity || 0), 0)
        const totalAfter = list.reduce((s, it) => {
            const qty = Number(it.quantity) || 0
            const unit = Number(it.price) || 0
            const bulkMin = Number(it.bulkMinQuantity)
            const hasBulk = Number.isFinite(bulkMin) && bulkMin > 0 && it.bulkSellPrice != null
            if (hasBulk && qty >= bulkMin) {
                const bulkPrice = Number(it.bulkSellPrice) || 0
                const bundles = Math.floor(qty / bulkMin)
                const bulkQty = bundles * bulkMin
                const rem = qty - bulkQty
                return s + bulkPrice * bulkQty + unit * rem
            }
            return s + unit * qty
        }, 0)
        return { totalCount, totalAfter }
    }, [items])

    // Keep in-memory cart in sync if localStorage 'om_cart' was deleted
    React.useEffect(() => {
        const checkCartPersisted = () => {
            try {
                const persisted = localStorage.getItem('om_cart')
                const hasPersist = typeof persisted === 'string' && persisted.length > 0
                if (!hasPersist) {
                    // If storage is gone but we still have items, clear the in-memory store
                    if (Object.keys(items || {}).length > 0) {
                        clear()
                    }
                }
            } catch { /* ignore */
            }
        }
        // run on mount
        checkCartPersisted()
        // and on typical signals
        window.addEventListener('focus', checkCartPersisted)
        document.addEventListener('visibilitychange', checkCartPersisted)
        window.addEventListener('storage', checkCartPersisted)
        return () => {
            window.removeEventListener('focus', checkCartPersisted)
            document.removeEventListener('visibilitychange', checkCartPersisted)
            window.removeEventListener('storage', checkCartPersisted)
        }
    }, [clear, items])


    // Read current delivery location from localStorage and show only Arabic name
    const raw = localStorage.getItem('deliveryLocation')
    let locationText = ''
    if (raw) {
        try {
            const obj = JSON.parse(raw)
            if (obj && typeof obj === 'object') {
                locationText = obj.nameAr || obj.name || ''
            } else {
                locationText = String(raw)
            }
        } catch {
            locationText = String(raw)
        }
    }
    if (!locationText) locationText = 'غير محدد'
    // let deliveryPercentage = (totalAfter / 100) * 100;

    // Hide sticky cart footer on the cart page
    const isCart = !!location?.pathname && location.pathname.startsWith('/cart')
    const showFooter = totalCount > 0 && !isCart

    // OTP flow (for header sign-in)
    const otpRef = React.useRef(null)
    const [authTick, setAuthTick] = React.useState(0) // force re-render on login
    const [menuOpen, setMenuOpen] = React.useState(false)

    const openLogin = React.useCallback(() => otpRef.current?.open(), [])

    // Visitor tracking
    React.useEffect(() => {
        try {
            let vid = localStorage.getItem('om_visitor_id')
            if (!vid) {
                vid = crypto.randomUUID ? crypto.randomUUID() : (Math.random().toString(36).substring(2) + Date.now().toString(36))
                localStorage.setItem('om_visitor_id', vid)
            }
            // Ping backend
            fetch(apiUrl('/api/analytics/visit'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visitorId: vid })
            }).catch(e => console.error('Analytics error:', e))
        } catch (e) {
            console.error('Visitor tracking failed', e)
        }
    }, [])

    React.useEffect(() => {
        const handler = () => openLogin()
        const unauthHandler = () => {
            handleLogout()
            openLogin()
        }
        window.addEventListener('om:openLogin', handler)
        window.addEventListener('om_unauthorized', unauthHandler)
        return () => {
            window.removeEventListener('om:openLogin', handler)
            window.removeEventListener('om_unauthorized', unauthHandler)
        }
    }, [openLogin])

    function handleLogout() {
        try {
            localStorage.removeItem('user')
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
        } catch { /* ignore */ }
        setAuthTick(v => v + 1)
    }

    function handleMyOrdersClick() {
        try {
            const token = localStorage.getItem('accessToken')
            if (token) {
                navigate('/my-orders')
            } else {
                openLogin()
            }
        } catch {
            openLogin()
        }
    }

    function openWhatsApp() {
        const phone = '970595566789'
        const text = encodeURIComponent('مرحباً! أحتاج مساعدة من فضلك.')
        const url = `https://wa.me/${phone}?text=${text}`
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    return showGate ? gateView : (
        <Layout style={{ minHeight: '100vh' }}>
            <Header style={{
                background: '#f4ede3',
                borderBottom: '1px solid #f0f0f0',
                position: 'sticky',
                top: 0,
                zIndex: 100,
                paddingInline: 0
            }}>
                <div style={{
                    ...containerStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    height: 64,
                    position: 'relative',
                }}>
                    {/* Right side: Menu button */}
                    <div>
                        <Button aria-label="القائمة" onClick={() => setMenuOpen(true)} icon={<MenuOutlined />} />
                    </div>

                    {/* Center: Logo */}
                    <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center' }}>
                        <Link to="/">
                            <img src="/logo.png" alt="لين غو" style={{ height: 40, objectFit: 'contain' }} />
                        </Link>
                    </div>

                    {/* Left side: Facebook link */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <a
                            href="https://www.facebook.com/leenmallps"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'flex' }}
                        >
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/b/b8/2021_Facebook_icon.svg"
                                alt="Facebook"
                                style={{ height: 32, width: 32 }}
                            />
                        </a>
                    </div>
                </div>
                {/* Reusable OTP flow instance */}
                <OtpAuthFlow ref={otpRef} onSuccess={() => setAuthTick(v => v + 1)} />
                <Drawer
                    open={menuOpen}
                    onClose={() => setMenuOpen(false)}
                    placement="right"
                    width={300}
                    title="القائمة"
                >
                    <div style={{ display: 'grid', gap: 12 }}>
                        {(() => {
                            try {
                                const accessToken = localStorage.getItem('accessToken')
                                if (accessToken) {
                                    let nm = null
                                    try {
                                        const uRaw = localStorage.getItem('user')
                                        if (uRaw) {
                                            const u = JSON.parse(uRaw)
                                            nm = u?.name || u?.phone || null
                                        }
                                    } catch { /* ignore */ }
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Typography.Text>مرحباً ({nm || 'مستخدم'})</Typography.Text>
                                            <Button onClick={() => { handleLogout(); setMenuOpen(false); }}>تسجيل الخروج</Button>
                                        </div>
                                    )
                                }
                                return (
                                    <Button type="primary" onClick={openLogin}>تسجيل الدخول</Button>
                                )
                            } catch {
                                return null
                            }
                        })()}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, border: '1px solid #f0f0f0', borderRadius: 8 }}>
                            <img style={{ width: 32, height: 32, objectFit: 'contain' }} src="/location-logo-mini.png" alt="" />
                            <div style={{ display: 'grid' }}>
                                <Typography.Text>موقع التوصيل الحالي</Typography.Text>
                                <Typography.Text strong>{locationText}</Typography.Text>
                            </div>
                            <div style={{ marginInlineStart: 'auto' }}>
                                <Button size="small" onClick={() => { setMenuOpen(false); navigate('/delivery-location'); }}>تغيير</Button>
                            </div>
                        </div>

                        <Button onClick={() => { setMenuOpen(false); handleMyOrdersClick(); }}>
                            طلباتي
                        </Button>
                        <Button onClick={() => { setMenuOpen(false); openWhatsApp(); }}>
                            تواصل معنا على واتس اب
                        </Button>
                        <Link to="/marketing" onClick={() => setMenuOpen(false)} style={{ display: 'block', marginTop: 8 }}>
                            عن التطبيق LeenGo (تسويق)
                        </Link>
                        <Link to="/support" onClick={() => setMenuOpen(false)} style={{ display: 'block', marginTop: 4 }}>
                            الدعم والمساعدة (التطبيق)
                        </Link>
                        <Link to="/privacy" onClick={() => setMenuOpen(false)} style={{ display: 'block', marginTop: 4 }}>
                            سياسة الخصوصية
                        </Link>
                        <Link to="/account-deletion" onClick={() => setMenuOpen(false)} style={{ display: 'block', marginTop: 4 }}>
                            حذف الحساب والبيانات
                        </Link>
                    </div>
                </Drawer>
            </Header>
            {/* Search bar under the header */}
            {/* <div style={{ background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ ...containerStyle, ...bodyStyle, paddingBlock: 12 }}>
                    <HeaderSearch navigate={navigate} />
                </div>
            </div> */}
            <Content style={{ paddingBlock: 'clamp(12px, 4vw, 24px)', paddingBottom: showFooter ? 135 : 0 }}>
                <div style={{ ...containerStyle, ...bodyStyle }}>
                    <Outlet context={{
                        outSideWorkingHours,
                        shift1Start,
                        shift1End,
                        shift2Start,
                        shift2End,
                        formatTimeArabic
                    }} />
                </div>
            </Content>

            {/* WhatsApp Float Button (expandable) */}
            <WhatsAppFloat expandedOffset={showFooter ? 80 : 24} />


            {showFooter && (
                <Footer style={{
                    textAlign: 'center',
                    background: '#ffffff',
                    position: 'fixed',
                    padding: '10px 0',
                    bottom: 0,
                    boxShadow: '0px 5px 10px #f55905',
                    left: 0,
                    right: 0,
                    width: '100%',
                    zIndex: 100
                }}>
                    {/*{(deliveryPercentage < 100) ? <div style={{*/}
                    {/*    textAlign: 'center',*/}
                    {/*}}>*/}
                    {/*    <span style={{*/}
                    {/*        color: '#f55905'*/}
                    {/*    }}>أضف {formatAmount(100 - totalAfter)} لتتمتّع بالتوصيل المجّاني</span>*/}
                    {/*    <Progress*/}
                    {/*        style={{*/}
                    {/*            marginBottom: 10,*/}
                    {/*            width: '90%'*/}
                    {/*        }}*/}
                    {/*        percent={deliveryPercentage}*/}
                    {/*        strokeColor='#f55905'*/}
                    {/*        showInfo={false}/>*/}
                    {/*</div> : <div style={{fontSize: 16, marginBottom: 10, color: '#f55905', fontWeight: 'bold'}}>*/}
                    {/*    🎉🎉 مبروك! التّوصيل مجّاني لأنّه طلبك فوق ال {formatAmount(100)}*/}
                    {/*</div>}*/}
                    <div
                        onClick={() => navigate('/cart')}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 16,
                            justifyContent: 'space-between',
                            gap: 12,
                            fontWeight: 'bold',
                            margin: '0 auto',
                            borderRadius: 40,
                            width: '90%',
                            padding: '12px 16px',
                            background: '#f55905',
                            color: '#fff'
                        }}
                    >
                        <div>
                            <Button shape={'circle'} style={{
                                background: '$ffffff',
                                marginLeft: 10,
                                fontWeight: 'bold',
                                color: '#f55905'
                            }}>{totalCount}</Button>
                            <span>عرض السّلة</span>
                        </div>
                        <div>
                            <span>الإجمالي: {formatAmount(totalAfter)}</span>
                        </div>
                    </div>
                </Footer>
            )}
        </Layout>
    )
}

function HeaderSearch({ navigate }) {
    const [value, setValue] = React.useState('')

    const onSearch = (val) => {
        const q = (val ?? '').trim()
        if (!q) return
        navigate(`/search?nameAr=${encodeURIComponent(q)}`)
    }

    // Auto trigger search as user types (debounced)
    React.useEffect(() => {
        const q = (value ?? '').trim()
        if (!q) return
        const t = setTimeout(() => {
            navigate(`/search?nameAr=${encodeURIComponent(q)}`)
        }, 400)
        return () => clearTimeout(t)
    }, [value, navigate])

    return (
        <div style={{ display: 'grid', gap: 8 }}>
            <Input.Search
                allowClear
                value={value}
                onChange={e => setValue(e.target.value)}
                onSearch={onSearch}
                placeholder="ابحث عن منتج..."
                enterButton="بحث"
                size="large"
                style={{ width: '100%' }}
            />
        </div>
    )
}

function WhatsAppFloat({ expandedOffset = 24 }) {
    const [expanded, setExpanded] = React.useState(false)

    const containerStyle = {
        position: 'fixed',
        right: 10,
        bottom: expandedOffset,
        height: 56,
        width: expanded ? '90%' : 56,
        transition: 'width 300ms ease',
        color: '#fff',
        borderRadius: 25,
        background: expanded ? '#f55905' : 'transparent',
        display: expanded ? 'flex' : 'block',
        padding: expanded ? '0 10px' : 0,
        alignItems: 'center',
        gap: 10,
        zIndex: 150,
        overflow: 'hidden',
        direction: 'ltr',
        userSelect: 'none'
    }

    // WhatsApp SVG icon (uses currentColor)


    return (
        <div
            onClick={() => {
                const phone = '970595566789';
                const text = encodeURIComponent('مرحباً! أحتاج مساعدة من فضلك.');
                const url = `https://wa.me/${phone}?text=${text}`;
                window.open(url, '_blank', 'noopener,noreferrer');
            }}
            style={containerStyle}>
            <svg style={{ width: 50, height: 50 }} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                    <path fillRule="evenodd" clipRule="evenodd"
                        d="M16 31C23.732 31 30 24.732 30 17C30 9.26801 23.732 3 16 3C8.26801 3 2 9.26801 2 17C2 19.5109 2.661 21.8674 3.81847 23.905L2 31L9.31486 29.3038C11.3014 30.3854 13.5789 31 16 31ZM16 28.8462C22.5425 28.8462 27.8462 23.5425 27.8462 17C27.8462 10.4576 22.5425 5.15385 16 5.15385C9.45755 5.15385 4.15385 10.4576 4.15385 17C4.15385 19.5261 4.9445 21.8675 6.29184 23.7902L5.23077 27.7692L9.27993 26.7569C11.1894 28.0746 13.5046 28.8462 16 28.8462Z"
                        fill="#BFC8D0"></path>
                    <path
                        d="M28 16C28 22.6274 22.6274 28 16 28C13.4722 28 11.1269 27.2184 9.19266 25.8837L5.09091 26.9091L6.16576 22.8784C4.80092 20.9307 4 18.5589 4 16C4 9.37258 9.37258 4 16 4C22.6274 4 28 9.37258 28 16Z"
                        fill="url(#paint0_linear_87_7264)"></path>
                    <path fillRule="evenodd" clipRule="evenodd"
                        d="M16 30C23.732 30 30 23.732 30 16C30 8.26801 23.732 2 16 2C8.26801 2 2 8.26801 2 16C2 18.5109 2.661 20.8674 3.81847 22.905L2 30L9.31486 28.3038C11.3014 29.3854 13.5789 30 16 30ZM16 27.8462C22.5425 27.8462 27.8462 22.5425 27.8462 16C27.8462 9.45755 22.5425 4.15385 16 4.15385C9.45755 4.15385 4.15385 9.45755 4.15385 16C4.15385 18.5261 4.9445 20.8675 6.29184 22.7902L5.23077 26.7692L9.27993 25.7569C11.1894 27.0746 13.5046 27.8462 16 27.8462Z"
                        fill="white"></path>
                    <path
                        d="M12.5 9.49989C12.1672 8.83131 11.6565 8.8905 11.1407 8.8905C10.2188 8.8905 8.78125 9.99478 8.78125 12.05C8.78125 13.7343 9.52345 15.578 12.0244 18.3361C14.438 20.9979 17.6094 22.3748 20.2422 22.3279C22.875 22.2811 23.4167 20.0154 23.4167 19.2503C23.4167 18.9112 23.2062 18.742 23.0613 18.696C22.1641 18.2654 20.5093 17.4631 20.1328 17.3124C19.7563 17.1617 19.5597 17.3656 19.4375 17.4765C19.0961 17.8018 18.4193 18.7608 18.1875 18.9765C17.9558 19.1922 17.6103 19.083 17.4665 19.0015C16.9374 18.7892 15.5029 18.1511 14.3595 17.0426C12.9453 15.6718 12.8623 15.2001 12.5959 14.7803C12.3828 14.4444 12.5392 14.2384 12.6172 14.1483C12.9219 13.7968 13.3426 13.254 13.5313 12.9843C13.7199 12.7145 13.5702 12.305 13.4803 12.05C13.0938 10.953 12.7663 10.0347 12.5 9.49989Z"
                        fill="white"></path>
                    <defs>
                        <linearGradient id="paint0_linear_87_7264" x1="26.5" y1="7" x2="4" y2="28"
                            gradientUnits="userSpaceOnUse">
                            <stop stopColor="#5BD066"></stop>
                            <stop offset="1" stopColor="#27B43E"></stop>
                        </linearGradient>
                    </defs>
                </g>
            </svg>
        </div>
    )
}
